import React from "react";
import { Checkbox, Typography } from "@material-ui/core";
import PhoneIcon from "@material-ui/icons/Phone";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import BusinessIcon from "@material-ui/icons/Business";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import Badge from "../../Badge/Badge";
import { useStyles } from "./AccessTypeResultCardStyles";
import { ALL_DAYS_ORDERED, getDayShortName, formatDateDisplay, findNextEnabledDay } from "./utils";

export type AuthorizationSchedule = {
  from?: string | null;
  to?: string | null;
  fromHour?: string | null;
  toHour?: string | null;
  days?: number[] | null;
  status?: "valid" | "invalid" | null;
};

type AccessTypeResultCardProps = {
  label: string;
  status?: string;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  managerName?: string;
  managerPhone?: string;
  type?: string;
  variant?: "vigente" | "guardia" | "gray" | "employee" | "fuera-de-rango" | "paso-previo";
  checkboxPosition?: "left" | "right";
  managerLabel?: string;
  phoneLabel?: string;
  disabled?: boolean;
  showCheckbox?: boolean;
  leftIcon?: React.ReactNode;
  authorizationSchedule?: AuthorizationSchedule;
  showAuthorizationDetails?: boolean;
  /** Paso previo por portería */
  accessByGuard?: boolean;
  /** Acceso suspendido */
  suspended?: boolean;
};

const AccessTypeResultCard: React.FC<AccessTypeResultCardProps> = ({
  label,
  status,
  checked = false,
  onCheckedChange,
  managerName,
  managerPhone,
  type,
  variant = "vigente",
  checkboxPosition = "left",
  managerLabel = "Anfitrión",
  phoneLabel = "Teléfono",
  disabled = false,
  showCheckbox = true,
  leftIcon,
  authorizationSchedule,
  showAuthorizationDetails = false,
  accessByGuard = false,
  suspended = false,
}) => {
  // Resolve variant dynamically if showAuthorizationDetails is true.
  // Precedencia: acceso suspendido > fuera de rango > paso previo por portería > vigente.
  let resolvedVariant = variant;
  if (showAuthorizationDetails && authorizationSchedule) {
    if (suspended || authorizationSchedule.status === "invalid") resolvedVariant = "fuera-de-rango";
    else if (accessByGuard) resolvedVariant = "paso-previo";
    else resolvedVariant = "vigente";
  }

  const hasAuthorizationDetails = Boolean(managerName || managerPhone);

  const classes = useStyles({
    variant: resolvedVariant,
    checkboxPosition,
    disabled,
    showAuthorizationDetails,
  });

  const handleClick = () => {
    if (disabled || !showCheckbox) return;
    onCheckedChange?.(!checked);
  };

  const isInvalid = showAuthorizationDetails && authorizationSchedule?.status === "invalid";
  const enabledDays = authorizationSchedule?.days || [];

  const nextDayText = React.useMemo(() => {
    if (!isInvalid || !authorizationSchedule) return null;
    return findNextEnabledDay(new Date(), enabledDays);
  }, [isInvalid, authorizationSchedule, enabledDays]);

  return (
    <div className={classes.container} onClick={handleClick}>
      <div className={classes.headerRow}>
        <div className={classes.titleGroup}>
          {showCheckbox && (
            <div className={classes.checkboxWrapper}>
              <Checkbox
                disabled={disabled}
                checked={checked}
                onChange={(e) => onCheckedChange?.(e.target.checked)}
                onClick={(e) => e.stopPropagation()}
                className={classes.checkbox}
                icon={<CheckBoxOutlineBlankIcon className={classes.checkboxIcon} />}
                checkedIcon={
                  <div className={classes.checkedIconWrapper}>
                    <CheckBoxIcon className={classes.checkboxIcon} />
                    <div className={classes.tickBackground} />
                  </div>
                }
              />
            </div>
          )}
          <div className={classes.left}>
            {leftIcon && <div className={classes.leftIconWrapper}>{leftIcon}</div>}
            <div className={classes.companyContainer}>
              <Typography variant="h6" className={classes.companyName}>
                {label}
              </Typography>
              {type && <div className={classes.typePill}>{type}</div>}
            </div>
          </div>
        </div>

        {hasAuthorizationDetails && (
          <div className={classes.detailsSection}>
            {managerPhone && (
              <div className={`${classes.detailItem} ${classes.phoneDetailItem}`}>
                {phoneLabel === "Empresa" ? (
                  <BusinessIcon className={classes.detailIcon} />
                ) : (
                  <PhoneIcon className={classes.detailIcon} />
                )}
                <div className={classes.detailText}>
                  <Typography className={classes.detailLabel}>{phoneLabel}</Typography>
                  <Typography className={classes.detailValue} title={managerPhone}>
                    {managerPhone}
                  </Typography>
                </div>
              </div>
            )}
            {managerName && (
              <div className={`${classes.detailItem} ${classes.managerDetailItem}`}>
                <PersonOutlineIcon className={classes.detailIcon} />
                <div className={classes.detailText}>
                  <Typography className={classes.detailLabel}>{managerLabel}</Typography>
                  <Typography className={classes.detailValue} title={managerName}>
                    {managerName}
                  </Typography>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {showAuthorizationDetails && authorizationSchedule && (
        <div className={classes.authDetailsRow}>
          <div className={classes.authStatusSection}>
            <div className={classes.authStatusHeader}>
              {isInvalid ? (
                <ErrorIcon className={classes.authStatusIconInvalid} />
              ) : (
                <CheckCircleIcon className={classes.authStatusIconValid} />
              )}
              <Typography
                className={isInvalid ? classes.authStatusTextInvalid : classes.authStatusTextValid}
              >
                {isInvalid ? "Autorización fuera de rango" : "Autorización vigente"}
              </Typography>
              {(accessByGuard || suspended) && (
                <div className={classes.flagBadgesRow}>
                  {suspended && <Badge variant="red" label="Acceso suspendido" />}
                  {accessByGuard && <Badge variant="orange" label="Paso previo por portería" />}
                </div>
              )}
            </div>
          </div>

          <div className={classes.authVigenciaSection}>
            <Typography className={classes.authVigenciaLabel}>Vigencia</Typography>
            <div className={classes.authVigenciaRow}>
              <CalendarTodayIcon className={classes.authVigenciaIcon} />
              <div className={classes.detailText}>
                <Typography className={classes.authVigenciaDate}>
                  {formatDateDisplay(authorizationSchedule.from)}{" "}
                  {authorizationSchedule.fromHour || "00:00"} hs
                </Typography>
                <Typography className={classes.authVigenciaDate}>
                  {formatDateDisplay(authorizationSchedule.to)}{" "}
                  {authorizationSchedule.toHour || "23:59"} hs
                </Typography>
              </div>
            </div>
          </div>

          {enabledDays.length > 0 && (
            <div className={classes.authDaysSection}>
              <Typography className={classes.authDaysLabel}>Días habilitados</Typography>
              <div className={classes.authDaysPillsRow}>
                {ALL_DAYS_ORDERED.map((dayNum) => {
                  const isDayEnabled = enabledDays.includes(dayNum);
                  let pillClass = classes.dayPillActiveInvalid;
                  if (isDayEnabled) {
                    pillClass = classes.dayPillActive;
                  } else if (isInvalid) {
                    pillClass = classes.dayPillActiveInvalid;
                  }

                  return (
                    <div key={dayNum} className={pillClass}>
                      {getDayShortName(dayNum)}
                    </div>
                  );
                })}
              </div>
              {isInvalid && nextDayText && (
                <div className={classes.nextDayHint}>
                  <InfoOutlinedIcon className={classes.nextDayHintIcon} />
                  <span>Próximo día habilitado: {nextDayText}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AccessTypeResultCard;
